import { useState } from "react";
import VpsUploadField from "@/components/base/VpsUploadField";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

interface Props {
  form: Invitation;
  onChange: <K extends keyof Invitation>(key: K, val: Invitation[K]) => void;
  tenantId?: string;
}

export default function MediaTab({ form, onChange, tenantId }: Props) {
  const [imgError, setImgError] = useState(false);

  const addGalleryUrl = (url: string) => {
    onChange("gallery_urls", [...(form.gallery_urls ?? []), url]);
  };

  const removeGalleryUrl = (idx: number) => {
    onChange(
      "gallery_urls",
      (form.gallery_urls ?? []).filter((_, i) => i !== idx),
    );
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const arr = [...(form.gallery_urls ?? [])];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    onChange("gallery_urls", arr);
  };

  const moveDown = (idx: number) => {
    const arr = [...(form.gallery_urls ?? [])];
    if (idx >= arr.length - 1) return;
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    onChange("gallery_urls", arr);
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Media & Assets</h3>
        <p className="text-slate-400 text-xs">Cover photo, background music and gallery images for the invitation.</p>
      </div>

      {/* Cover image upload */}
      <div>
        {tenantId ? (
          <VpsUploadField
            value={form.cover_image_url ?? null}
            onChange={(url) => {
              setImgError(false);
              onChange("cover_image_url", url);
            }}
            tenantId={tenantId}
            entityType="digital_invitation"
            entityId={form.id || undefined}
            label="Cover Image"
            accept="image/*"
            showPreview
            previewHeight="h-48"
            helperText="This image appears as the hero/header of the invitation."
            folder="invitations"
            maxSizeMB={10}
          />
        ) : (
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Cover Image URL
            </label>
            <input
              type="url"
              value={form.cover_image_url ?? ""}
              onChange={(e) => {
                setImgError(false);
                onChange("cover_image_url", e.target.value || null);
              }}
              placeholder="https://..."
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
            />
            {form.cover_image_url && !imgError && (
              <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 h-48 bg-slate-100">
                <img
                  src={form.cover_image_url}
                  alt="Cover preview"
                  className="w-full h-full object-cover object-top"
                  onError={() => setImgError(true)}
                />
              </div>
            )}
            {imgError && (
              <div className="mt-3 rounded-xl border border-red-100 bg-red-50 h-16 flex items-center justify-center gap-2 text-red-400 text-xs">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-image-2-line"></i>
                </span>
                Could not load image — check the URL
              </div>
            )}
          </div>
        )}
      </div>

      {/* Music URL */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Background Music URL
        </label>
        <input
          type="url"
          value={form.music_url ?? ""}
          onChange={(e) => onChange("music_url", e.target.value || null)}
          placeholder="https://... (direct .mp3 link)"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
        />
        {form.music_url && (
          <div className="mt-2">
            <audio controls src={form.music_url} className="w-full h-8 mt-1" />
          </div>
        )}
        <p className="text-slate-400 text-xs mt-1.5">Direct MP3 link. Plays automatically (muted by default) when guests open the invitation.</p>
      </div>

      {/* Gallery */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Gallery Images
          <span className="ml-2 text-slate-300 normal-case font-normal">
            ({(form.gallery_urls ?? []).length} added)
          </span>
        </label>

        {/* Add gallery upload */}
        {tenantId && (
          <div className="mb-4">
            <VpsUploadField
              value={null}
              onChange={(url) => {
                if (url) addGalleryUrl(url);
              }}
              tenantId={tenantId}
              entityType="digital_invitation"
              entityId={form.id || undefined}
              label=""
              accept="image/*"
              showPreview={false}
              helperText="Upload images to add them to the gallery."
              folder="invitations"
              maxSizeMB={10}
            />
          </div>
        )}

        {/* Gallery grid */}
        {(form.gallery_urls ?? []).length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(form.gallery_urls ?? []).map((url, idx) => (
              <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-100 h-28">
                <img
                  src={url}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-full object-cover object-top"
                />
                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                  <button
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 disabled:opacity-30 text-white rounded-lg transition-colors cursor-pointer"
                    title="Move up"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => removeGalleryUrl(idx)}
                    className="w-7 h-7 flex items-center justify-center bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors cursor-pointer"
                    title="Remove"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => moveDown(idx)}
                    disabled={idx >= (form.gallery_urls ?? []).length - 1}
                    className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 disabled:opacity-30 text-white rounded-lg transition-colors cursor-pointer"
                    title="Move down"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
                {/* Index badge */}
                <div className="absolute bottom-1.5 left-1.5 w-5 h-5 flex items-center justify-center bg-black/60 text-white text-[10px] rounded-md">
                  {idx + 1}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-300 mx-auto mb-2">
              <i className="ri-image-2-line text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm">No gallery images yet</p>
            <p className="text-slate-300 text-xs mt-1">Upload images above to build the gallery</p>
          </div>
        )}
      </div>
    </div>
  );
}